/**
 * Plan Builder Types
 *
 * Data model for the plan personalization flow.
 * Collects the minimum info needed to tailor the homepage.
 */

import type { HomepageGender, HomepageGoal } from "../../homepage.types";
import type { FertilityIntent } from "@src/packages/fertility-intent";
import type { AcceptedTreatmentPlanDraft } from "../treatmentPlanTypes";
import { INITIAL_ACCEPTED_TREATMENT_PLAN } from "../treatmentPlanTypes";

// ─── Past Cycle ───

export type PastCycleType =
  | "ivf"
  | "iui"
  | "egg-freezing"
  | "embryo-freezing"
  | "medicated-cycle"
  | "other";

export type PastCycleOutcome =
  "successful" | "unsuccessful" | "cancelled" | "ongoing" | "prefer-not-to-say";

export type PastCycle = {
  type: PastCycleType;
  count: number;
  /** One outcome per cycle — length should match count */
  outcomes: PastCycleOutcome[];
};

// ─── Condition (disease enum values + other API fields) ───

export type Condition =
  // diseases table values
  | "pcos"
  | "endometriosis"
  | "thyroid_disease"
  | "diminished_ovarian_reserve"
  | "primary_ovarian_insufficiency"
  | "fibroids"
  // other tables
  | "tubal_causes"
  | "ovarian_surgery"
  | "none_or_unsure"
  | "irregular"
  | "loss"
  | "cancer"
  | "male-factor"
  | "other";

// ─── Partner Status ───

export type PartnerStatus = "with-partner" | "using-donor" | "solo";

// ─── Known Test Results ───

export type KnownResults = {
  hasAMH: boolean;
  amhValue?: number;
  hasFSH: boolean;
  fshValue?: number;
  hasSA: boolean;
  saStatus?: "normal" | "borderline" | "low" | "unknown";
};

// ─── Builder State ───

export type TreatmentModality = Extract<
  FertilityIntent["plannedModalities"][number],
  "ivf" | "iui" | "fet" | "not-sure"
>;

export type PlanBuilderState = {
  // Pre-filled from patient record — used for goal-specific branching
  gender: HomepageGender | null;
  /** Canonical lifecycle stage from the patient fertility-intent record. */
  stage: FertilityIntent["stage"] | null;

  // Step 1: Goal recap (pre-filled from onboarding)
  goal: HomepageGoal | null;

  // Date of birth — critical for age-based personalization
  dateOfBirth: string | null;

  // Treatment modalities being considered (pregnancy goal only, multi-select)
  modalities: TreatmentModality[];

  // The current accepted plan is distinct from modalities being considered.
  acceptedPlan: AcceptedTreatmentPlanDraft;
  acceptedPlanAcknowledged: boolean;

  // Step 2: Past cycles
  hasPastCycles: boolean | null;
  pastCycles: PastCycle[];

  // Step 3: Medical conditions
  conditions: Condition[];

  // Step 4: Ethnicity (multi-select)
  ethnicity: string[];

  // Step 5: Partner/donor (goal-specific)
  partnerStatus: PartnerStatus | null;

  // Step 6: Known test results (goal-specific)
  knownResults: KnownResults;

  // Step 7: Location + insurance
  zipCode: string | null;
  hasInsurance: boolean | null;
  insuranceCarrier: string | null;
  hasEmployerBenefit: boolean | null;
  employerBenefit: string | null;

  // Step 7b: Clinic status (preserve goals only)
  clinicStatus: "no-clinic" | "researching" | "at-clinic" | null;

  // Step 8: Timeline
  timeline: "asap" | "3-months" | "6-months" | "exploring" | null;

  // Step 9: Free text
  freeText: string;

  // Meta
};

export const INITIAL_BUILDER_STATE: PlanBuilderState = {
  gender: null,
  stage: null,
  goal: null,
  dateOfBirth: null,
  modalities: [],
  acceptedPlan: INITIAL_ACCEPTED_TREATMENT_PLAN,
  acceptedPlanAcknowledged: false,
  hasPastCycles: null,
  pastCycles: [],
  conditions: [],
  ethnicity: [],
  partnerStatus: null,
  clinicStatus: null,
  knownResults: {
    hasAMH: false,
    hasFSH: false,
    hasSA: false,
  },
  zipCode: null,
  hasInsurance: null,
  insuranceCarrier: null,
  hasEmployerBenefit: null,
  employerBenefit: null,
  timeline: null,
  freeText: "",
};

// ─── Screen Names ───

export type PlanBuilderScreen =
  | "GoalRecap"
  | "ModalityScreen"
  | "AcceptedPlan"
  | "PastCycles"
  | "PastCycleDetails"
  | "Conditions"
  | "Ethnicity"
  | "PartnerStatus"
  | "ClinicStatus"
  | "KnownResults"
  | "LocationInsurance"
  | "Timeline"
  | "FreeText"
  | "Building"
  | "PlanReveal";
